
import React from 'react';
import { cn } from '@/lib/utils';
import NavMenuItems from './NavMenuItems';
import NavLink from './NavLink';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslation } from '@/lib/i18n';

import { Settings, ChevronDown, Calendar, BookOpen, GraduationCap, Briefcase, Gift, Tag, Lightbulb, Compass, Globe, TrendingUp, Building2 } from 'lucide-react';
import { ConsortiumInvitations } from '../notifications/ConsortiumInvitations';
import { NetworkingNotificationBadge } from '../networking/NetworkingNotificationBadge';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";

interface DesktopNavigationProps {
  isHomePage: boolean;
  isScrolled: boolean;
  user: any;
  member: any;
  isAdmin: boolean;
  isMember: boolean;
}

const DesktopNavigation: React.FC<DesktopNavigationProps> = React.memo(({ isHomePage, isScrolled, user, member, isAdmin, isMember }) => {
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  return (
    <nav className="hidden md:flex items-center space-x-5">
      {/* Visiteurs : Vision & Mission dropdown */}
      {!isMember && !isAdmin && (
        <NavigationMenu>
          <NavigationMenuList>
            <NavigationMenuItem>
              <NavigationMenuTrigger 
                className="font-medium transition-all duration-300 text-white hover:text-singu-yellow focus:text-singu-yellow data-[state=open]:text-singu-yellow bg-transparent border-0 shadow-none"
              >
                {t('visionMission')}
              </NavigationMenuTrigger>
              <NavigationMenuContent>
                <ul className="grid gap-3 p-4 w-[400px]">
                  <NavMenuItems isHomePage={isHomePage} isScrolled={isScrolled} />
                </ul>
              </NavigationMenuContent>
            </NavigationMenuItem>
          </NavigationMenuList>
        </NavigationMenu>
      )}

      {/* Membres/Admin : Tableau de bord */}
      {(isMember || isAdmin) && (
        <NavLink to="/dashboard" isPage isScrolled={isScrolled} isHomePage={isHomePage} className="font-semibold text-singu-yellow">
          Tableau de bord
        </NavLink>
      )}

      {/* Commun : Annuaire */}
      <NavLink to="/members" isPage isScrolled={isScrolled} isHomePage={isHomePage}>Annuaire</NavLink>

      {/* Commun : Networking */}
      <NetworkingNotificationBadge>
        <NavLink to="/networking" isPage isScrolled={isScrolled} isHomePage={isHomePage}>Networking</NavLink>
      </NetworkingNotificationBadge>
      
      {/* Commun : Opportunités */}
      <NavLink to="/opportunities" isPage isScrolled={isScrolled} isHomePage={isHomePage}>Opportunités</NavLink>

      {/* Menu "Explorer" — navigation métier catégorisée */}
      <NavigationMenu>
        <NavigationMenuList>
          <NavigationMenuItem>
            <NavigationMenuTrigger 
              className="font-medium transition-all duration-300 text-white hover:text-singu-yellow focus:text-singu-yellow data-[state=open]:text-singu-yellow bg-transparent border-0 shadow-none"
            >
              <Compass className="h-4 w-4 mr-1" />
              Explorer
            </NavigationMenuTrigger>
            <NavigationMenuContent>
              <ul className="grid gap-1 p-3 w-[280px]">
                {/* Découvrir */}
                <li className="px-3 pt-1 pb-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Découvrir</li>
                <li>
                  <NavLink to="/events" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Événements</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/insights" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                    <BookOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Insights</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/workshops" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                    <GraduationCap className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Workshops</span>
                  </NavLink>
                </li>

                {/* Carrière */}
                <li className="my-1 border-t border-border" />
                <li className="px-3 pt-1 pb-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Carrière</li>
                <li>
                  <NavLink to="/jobs" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                    <Briefcase className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Job Board</span>
                  </NavLink>
                </li>
                <li>
                  <NavLink to="/formation" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                    <GraduationCap className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">Formation</span>
                  </NavLink>
                </li>

                {/* Business (membres uniquement) */}
                {(isMember || isAdmin) && (
                  <>
                    <li className="my-1 border-t border-border" />
                    <li className="px-3 pt-1 pb-0.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Business</li>
                    <li>
                      <NavLink to="/deals" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                        <Tag className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Deals</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/perks" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                        <Gift className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Avantages</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/commissions" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                        <Lightbulb className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Commissions</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/business" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">Business</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink to="/international" isPage className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-accent/50 transition-colors text-foreground">
                        <Globe className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium text-sm">International</span>
                      </NavLink>
                    </li>
                  </>
                )}
              </ul>
            </NavigationMenuContent>
          </NavigationMenuItem>
        </NavigationMenuList>
      </NavigationMenu>

      {user && <ConsortiumInvitations />}

      {isAdmin && (
        <NavLink 
          to="/admin" 
          isPage 
          isScrolled={isScrolled}
          isHomePage={isHomePage}
          className="flex items-center gap-2 text-singu-yellow hover:underline"
        >
          <Settings className="h-4 w-4" />
          Admin
        </NavLink>
      )}
    </nav>
  );
});

export default DesktopNavigation;
